// Birthday proximity — Florida clock, same as clinicDay.
//
// Owner, 2026-08-19: highlight when a birthday is about 5 days away, and
// surface those on My Desk. The profile already stores MM/DD. This file is
// the one place that turns that into "how many clinic days until it".

import { clinicToday } from './clinicDay';

const MD = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/;

const ALERT_WINDOW_DAYS = 5;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Valid calendar Y-M-D for this MM/DD in year y, or null (e.g. 02/31).
 * 02/29 on a non-leap year is 02/28 — they still get the day, not a skip. */
function inYear(year: number, month: number, day: number): string | null {
  if (month === 2 && day === 29) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    day = leap ? 29 : 28;
  }
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) {
    return null;
  }
  return `${year}-${pad(month)}-${pad(day)}`;
}

function daysBetween(fromYmd: string, toYmd: string): number {
  const [fy, fm, fd] = fromYmd.split('-').map(Number);
  const [ty, tm, td] = toYmd.split('-').map(Number);
  const from = new Date(fy, fm - 1, fd).getTime();
  const to = new Date(ty, tm - 1, td).getTime();
  return Math.round((to - from) / 86_400_000);
}

/** Clinic days until the next birthday. 0 is today. Null if MM/DD is junk. */
export function daysUntilBirthday(birthdayMd: string | null | undefined, now: Date = new Date()): number | null {
  const md = (birthdayMd ?? '').trim();
  const m = MD.exec(md);
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  const today = clinicToday(now);
  const year = Number(today.slice(0, 4));
  const thisYear = inYear(year, month, day);
  const nextYear = inYear(year + 1, month, day);
  if (!thisYear || !nextYear) return null;
  const candidate = thisYear >= today ? thisYear : nextYear;
  return daysBetween(today, candidate);
}

/** True when the birthday is today or within the next 5 clinic days. */
export function birthdaySoon(birthdayMd: string | null | undefined, now: Date = new Date()): boolean {
  const days = daysUntilBirthday(birthdayMd, now);
  return days !== null && days >= 0 && days <= ALERT_WINDOW_DAYS;
}
